import {
  IonAlert,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonInput,
  IonModal,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api/client.js";
import { avatarColor } from "../utils/avatarColor.js";
import { currentWeekRange, dateEnd, dateInput, dateStart } from "../utils/date.js";
import DateRangeNavigator from "./DateRangeNavigator.jsx";

const blankForm = {
  clientId: "",
  occurrenceIds: [],
  amount: "",
  method: "Cash",
  bankState: "Complete",
  paymentDate: "",
  reference: "",
  notes: "",
};

function currency(value) {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(Number(value) || 0);
}

function paymentDate(value) {
  return new Intl.DateTimeFormat("en-AU", {
    dateStyle: "medium",
  }).format(new Date(value));
}

function visitDate(value) {
  return new Intl.DateTimeFormat("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date(value));
}

function selectedTotal(occurrences, occurrenceIds) {
  const selected = new Set(occurrenceIds);

  return occurrences
    .filter((occurrence) => selected.has(occurrence.id))
    .reduce((sum, occurrence) => sum + Number(occurrence.remaining), 0);
}

function paymentOccurrenceRows(payment) {
  return payment.occurrences.map((allocation) => ({
    id: allocation.scheduleOccurrence.id,
    scheduledStart: allocation.scheduleOccurrence.scheduledStart,
    label:
      allocation.scheduleOccurrence.job.internalLabel
      || allocation.scheduleOccurrence.job.client.name,
    remaining: Number(allocation.amountAllocated),
    allocated: Number(allocation.amountAllocated),
  }));
}

export default function PaymentsPanel({ onToast }) {
  const [range, setRange] = useState(currentWeekRange);
  const [view, setView] = useState("due");
  const [dashboard, setDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [isSaving, setIsSaving] = useState(false);
  const [returnPayment, setReturnPayment] = useState(null);
  const [confirmEditor, setConfirmEditor] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);

    try {
      const query = new URLSearchParams({
        from: dateStart(range.startDate).toISOString(),
        to: dateEnd(range.endDate).toISOString(),
      });
      setDashboard(
        await apiFetch(`/payments?${query.toString()}`, {
          showGlobalLoading: false,
        }),
      );
    } catch (err) {
      onToast(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [onToast, range.endDate, range.startDate]);

  useEffect(() => {
    load();
  }, [load]);

  const editorOccurrences = useMemo(() => {
    if (!editor) {
      return [];
    }

    return editor.mode === "create"
      ? editor.group.occurrences
      : paymentOccurrenceRows(editor.payment);
  }, [editor]);

  function openDue(group) {
    const occurrenceIds = group.occurrences.map((occurrence) => occurrence.id);

    setEditor({ mode: "create", group });
    setForm({
      ...blankForm,
      clientId: group.client.id,
      occurrenceIds,
      amount: selectedTotal(group.occurrences, occurrenceIds).toFixed(2),
      paymentDate: dateInput(new Date()),
    });
  }

  function openPending(payment) {
    const occurrences = paymentOccurrenceRows(payment);
    const occurrenceIds = occurrences.map((occurrence) => occurrence.id);

    setEditor({ mode: "edit", payment });
    setForm({
      ...blankForm,
      clientId: payment.clientId,
      occurrenceIds,
      amount: Number(payment.amount).toFixed(2),
      method: "BankTransfer",
      bankState: "Pending",
      reference: payment.reference || "",
      notes: payment.notes || "",
    });
  }

  function toggleOccurrence(occurrenceId, checked) {
    const occurrenceIds = checked
      ? [...new Set([...form.occurrenceIds, occurrenceId])]
      : form.occurrenceIds.filter((id) => id !== occurrenceId);

    setForm({
      ...form,
      occurrenceIds,
      amount: selectedTotal(editorOccurrences, occurrenceIds).toFixed(2),
    });
  }

  async function savePayment() {
    if (!editor || form.occurrenceIds.length === 0) {
      onToast("Select at least one visit.");

      return;
    }

    const status = form.method === "Cash" ? "Complete" : form.bankState;

    if (editor.mode === "create" && status === "Complete" && !form.paymentDate) {
      onToast("Select the payment date.");

      return;
    }

    setIsSaving(true);

    try {
      const body = {
        clientId: form.clientId,
        occurrenceIds: form.occurrenceIds,
        amount: Number(form.amount),
        reference: form.reference,
        notes: form.notes,
      };

      if (editor.mode === "create") {
        await apiFetch("/payments", {
          method: "POST",
          body: {
            ...body,
            method: form.method,
            status,
            ...(status === "Complete"
              ? { paidAt: dateStart(form.paymentDate).toISOString() }
              : {}),
          },
          showGlobalLoading: false,
        });
      } else {
        await apiFetch(`/payments/pending/${editor.payment.id}`, {
          method: "PUT",
          body,
          showGlobalLoading: false,
        });
      }

      setEditor(null);
      await load();
      onToast(editor.mode === "create" ? "Payment recorded." : "Pending payment updated.");
    } catch (err) {
      onToast(err.message);
    } finally {
      setIsSaving(false);
    }
  }

  function openConfirmPayment(payment) {
    setConfirmEditor({
      payment,
      paymentDate: dateInput(new Date()),
    });
  }

  async function confirmPayment() {
    if (!confirmEditor?.paymentDate) {
      onToast("Select the payment date.");

      return;
    }

    setIsConfirming(true);

    try {
      await apiFetch(`/payments/confirm/${confirmEditor.payment.id}`, {
        method: "PATCH",
        body: {
          paidAt: dateStart(confirmEditor.paymentDate).toISOString(),
        },
        showGlobalLoading: false,
      });
      setConfirmEditor(null);
      await load();
      onToast("Bank transfer marked as received.");
    } catch (err) {
      onToast(err.message);
    } finally {
      setIsConfirming(false);
    }
  }

  async function returnToDue() {
    if (!returnPayment) {
      return;
    }

    try {
      await apiFetch(`/payments/return-to-due/${returnPayment.id}`, {
        method: "DELETE",
        showGlobalLoading: false,
      });
      setReturnPayment(null);
      await load();
      onToast("Payment returned to Due.");
    } catch (err) {
      onToast(err.message);
    }
  }

  function renderDue() {
    if (!dashboard?.due.length) {
      return <p className="payment-empty">No payments are due in this date range.</p>;
    }

    return dashboard.due.map((group) => (
      <article className="payment-card" key={group.client.id}>
        <div className="payment-card-main">
          <div
            className="payment-avatar"
            style={{ "--payment-avatar-color": avatarColor(group.client.id) }}
          >
            {group.client.name.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h3>{group.client.name}</h3>
            <p>{group.occurrences.length} {group.occurrences.length === 1 ? "visit" : "visits"}</p>
            <strong className="payment-due">{currency(group.remaining)} due</strong>
          </div>
        </div>
        <IonButton onClick={() => openDue(group)}>Record Payment</IonButton>
      </article>
    ));
  }

  function renderPending() {
    if (!dashboard?.pending.length) {
      return <p className="payment-empty">No bank transfers are awaiting payment.</p>;
    }

    return dashboard.pending.map((payment) => (
      <article className="payment-card" key={payment.id}>
        <div className="payment-card-main">
          <div
            className="payment-avatar"
            style={{ "--payment-avatar-color": avatarColor(payment.client.id) }}
          >
            {payment.client.name.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h3>{payment.client.name}</h3>
            <p>Bank transfer pending</p>
            <strong>{currency(payment.amount)}</strong>
          </div>
        </div>
        <div className="payment-card-actions">
          <IonButton size="small" onClick={() => openConfirmPayment(payment)}>Confirm Received</IonButton>
          <IonButton size="small" fill="outline" onClick={() => openPending(payment)}>Edit</IonButton>
          <IonButton size="small" fill="clear" color="danger" onClick={() => setReturnPayment(payment)}>
            Return to Due
          </IonButton>
        </div>
      </article>
    ));
  }

  function renderPaid() {
    if (!dashboard?.paid.length) {
      return <p className="payment-empty">No payments were recorded in this date range.</p>;
    }

    return dashboard.paid.map((payment) => (
      <article className="payment-card paid" key={payment.id}>
        <div className="payment-card-main">
          <div
            className="payment-avatar"
            style={{ "--payment-avatar-color": avatarColor(payment.client.id) }}
          >
            {payment.client.name.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h3>{payment.client.name}</h3>
            <p>{payment.method.label} · Paid {paymentDate(payment.paidAt || payment.createdAt)}</p>
            <p>{payment.occurrences.length} {payment.occurrences.length === 1 ? "visit" : "visits"} · Recorded by {payment.createdBy.name}</p>
            <strong>{currency(payment.amount)}</strong>
          </div>
        </div>
      </article>
    ));
  }

  function renderList() {
    if (view === "due") {
      return renderDue();
    }
    if (view === "pending") {
      return renderPending();
    }
    if (view === "paid") {
      return renderPaid();
    }

    return (
      <>
        <h2 className="payment-list-heading">Due</h2>
        {renderDue()}
        <h2 className="payment-list-heading">Pending</h2>
        {renderPending()}
        <h2 className="payment-list-heading">Paid</h2>
        {renderPaid()}
      </>
    );
  }

  return (
    <>
      <section className="payments-toolbar">
        <DateRangeNavigator value={range} onChange={setRange} />

        <div className="payments-summary">
          <div>
            <span>Due</span>
            <strong>{currency(dashboard?.summary.due)}</strong>
          </div>
          <div>
            <span>Pending</span>
            <strong>{currency(dashboard?.summary.pending)}</strong>
          </div>
          <div>
            <span>Paid</span>
            <strong>{currency(dashboard?.summary.paid)}</strong>
          </div>
        </div>

        <IonSegment value={view} onIonChange={(event) => setView(event.detail.value || "due")}>
          <IonSegmentButton value="due">Due</IonSegmentButton>
          <IonSegmentButton value="pending">Pending</IonSegmentButton>
          <IonSegmentButton value="paid">Paid</IonSegmentButton>
          <IonSegmentButton value="all">All</IonSegmentButton>
        </IonSegment>
      </section>

      <section className="payment-list">
        {isLoading ? <div className="payments-loading" aria-label="Loading payments" /> : renderList()}
      </section>

      <IonModal isOpen={Boolean(editor)} onDidDismiss={() => setEditor(null)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{editor?.mode === "edit" ? "Edit Pending Payment" : "Record Payment"}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setEditor(null)}>Close</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <main className="page-pad">
            <section className="panel form-grid">
              <div>
                <h2 className="payment-modal-client">
                  {editor?.mode === "create" ? editor.group.client.name : editor?.payment.client.name}
                </h2>
                <p>Select the visits this payment covers. Partial payments are allocated to the oldest visit first.</p>
              </div>

              <div className="payment-occurrence-list">
                {editorOccurrences.map((occurrence) => (
                  <label className="payment-occurrence-row" key={occurrence.id}>
                    <IonCheckbox
                      checked={form.occurrenceIds.includes(occurrence.id)}
                      onIonChange={(event) => toggleOccurrence(occurrence.id, event.detail.checked)}
                    />
                    <span>
                      <strong>{occurrence.label}</strong>
                      <small>{visitDate(occurrence.scheduledStart)}</small>
                    </span>
                    <strong>{currency(occurrence.remaining)}</strong>
                  </label>
                ))}
              </div>

              <IonInput
                className="metora-input"
                label="Payment Amount"
                labelPlacement="stacked"
                type="number"
                min="0.01"
                step="0.01"
                value={form.amount}
                onIonInput={(event) => setForm({ ...form, amount: event.detail.value ?? "" })}
              />

              {editor?.mode === "create" ? (
                <IonSelect
                  className="metora-input"
                  label="Payment Method"
                  labelPlacement="stacked"
                  value={form.method}
                  onIonChange={(event) => {
                    const method = event.detail.value;
                    setForm({
                      ...form,
                      method,
                      ...(method === "BankTransfer" ? { bankState: "Complete" } : {}),
                    });
                  }}
                >
                  <IonSelectOption value="Cash">Cash</IonSelectOption>
                  <IonSelectOption value="BankTransfer">Bank Transfer</IonSelectOption>
                </IonSelect>
              ) : null}

              {editor?.mode === "create"
                && (form.method === "Cash" || form.bankState === "Complete") ? (
                  <IonInput
                    className="metora-input"
                    label="Payment Date"
                    labelPlacement="stacked"
                    type="date"
                    value={form.paymentDate}
                    onIonInput={(event) => setForm({
                      ...form,
                      paymentDate: event.detail.value ?? "",
                    })}
                  />
                ) : null}

              {editor?.mode === "create" && form.method === "BankTransfer" ? (
                <IonSelect
                  className="metora-input"
                  label="Bank Transfer Status"
                  labelPlacement="stacked"
                  value={form.bankState}
                  onIonChange={(event) => setForm({ ...form, bankState: event.detail.value })}
                >
                  <IonSelectOption value="Pending">Awaiting Transfer</IonSelectOption>
                  <IonSelectOption value="Complete">Received</IonSelectOption>
                </IonSelect>
              ) : null}

              <IonInput
                className="metora-input"
                label="Reference"
                labelPlacement="stacked"
                value={form.reference}
                onIonInput={(event) => setForm({ ...form, reference: event.detail.value ?? "" })}
              />
              <IonTextarea
                className="metora-input"
                label="Notes"
                labelPlacement="stacked"
                autoGrow
                value={form.notes}
                onIonInput={(event) => setForm({ ...form, notes: event.detail.value ?? "" })}
              />

              <IonButton expand="block" disabled={isSaving} onClick={savePayment}>
                {isSaving
                  ? "Saving..."
                  : editor?.mode === "edit"
                    ? "Update Pending Payment"
                    : form.method === "Cash" || form.bankState === "Complete"
                      ? "Record Payment"
                      : "Mark as Pending"}
              </IonButton>
            </section>
          </main>
        </IonContent>
      </IonModal>

      <IonModal
        isOpen={Boolean(confirmEditor)}
        onDidDismiss={() => {
          if (!isConfirming) {
            setConfirmEditor(null);
          }
        }}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>Confirm Bank Transfer</IonTitle>
            <IonButtons slot="end">
              <IonButton
                disabled={isConfirming}
                onClick={() => setConfirmEditor(null)}
              >
                Close
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <main className="page-pad">
            <section className="panel form-grid">
              <p>Choose the date the payment reached the account.</p>
              <IonInput
                className="metora-input"
                label="Payment Date"
                labelPlacement="stacked"
                type="date"
                value={confirmEditor?.paymentDate || ""}
                onIonInput={(event) => setConfirmEditor({
                  ...confirmEditor,
                  paymentDate: event.detail.value ?? "",
                })}
              />
              <IonButton
                expand="block"
                disabled={isConfirming || !confirmEditor?.paymentDate}
                onClick={confirmPayment}
              >
                {isConfirming ? "Confirming..." : "Confirm Received"}
              </IonButton>
            </section>
          </main>
        </IonContent>
      </IonModal>

      <IonAlert
        cssClass="metora-alert"
        isOpen={Boolean(returnPayment)}
        header="Return payment to Due?"
        message="This removes the pending bank transfer and restores its visits to the outstanding balance."
        buttons={[
          { text: "Keep Pending", role: "cancel" },
          { text: "Return to Due", role: "destructive", handler: returnToDue },
        ]}
        onDidDismiss={() => setReturnPayment(null)}
      />
    </>
  );
}
